import NiceModal from '@ebay/nice-modal-react';
import { enqueueSnackbar } from 'notistack';
import { forEachObjIndexed, includes, isEmpty, omit, pickAll, propOr } from 'ramda';
import { isNilOrEmpty, isNilOrEmptyString } from 'ramda-extension';

import { getCduUserLogon } from '@gef-ui/cdu-user/selectors';
import { replaceData, resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';
import { getMessage } from '@gef-ui/localization/messages';
import { getFilesFromBlobUrls } from '@gef-ui/utils';

import { codelists } from 'constants/codelists';
import { COMPANY, EMPTY_VALUES } from 'constants/general';
import MODALS from 'constants/modals';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import {
	addSingleErrorByPageAndId,
	removeErrorByPageAndId,
	resetErrorsByPage,
} from 'features/common/submitFormDialog/slice';
import {
	dialogs as resolverSettingsDialogs,
	radioOptionsIds,
	searchUserTypes,
} from 'features/other/resolverSettings/constants';
import { getUserDataFromDBFlag } from 'features/other/resolverSettings/selectors';
import {
	BACKOFFICE_FORM_NAME,
	FIELDS,
	MANUAL_LEAD_FORM_NAME,
	MOBILE_SCAN_FORM_NAME,
	SELLER_REQUEST_FORM_NAME,
} from 'features/views/taskCreatingPages/form';
import userSearch from 'features/views/userSearch';
import { formatPhoneNumber, getCodelistByName } from 'utils';
import { fromIsoDateToDate } from 'utils/apiDateTime';

import { defaultTaskSubtypeBackoffice, dialogs, partnerFields } from './constants';
import { getDocumentIds, getPolicyCompanyId, getSelectedClient, getSelectedPolicy } from './selectors';
import {
	addDocumentId,
	reset,
	setPartnerEditability,
	setPolicyCompanyId,
	setPolicyEditability,
	setSelectedClient,
	setSelectedPolicy,
	setTaskIsBeingCreated,
} from './slice';
import {
	bodyFactory,
	calculateDefaultDates,
	findBackOfficeSegmentValue,
	getCorrectDefaultTaskTypeValue,
} from './utils';

export const startupDetailInfo =
	(formName) =>
	async (dispatch, getState, { sendControlNext }) => {
		const codelist = getCodelistByName(codelists.taskTypes)(getState());
		const taskTypeId = getCorrectDefaultTaskTypeValue(formName);

		if (formName !== MANUAL_LEAD_FORM_NAME) {
			sendControlNext(formName, setValue({ fieldName: FIELDS.taskType, value: taskTypeId }));
		}

		if (formName === BACKOFFICE_FORM_NAME) {
			sendControlNext(
				formName,
				setValue({ fieldName: FIELDS.taskSubTypeId, value: defaultTaskSubtypeBackoffice }),
			);
		}

		// setting default values for fulfillDate and warningDate
		if (includes(formName, [BACKOFFICE_FORM_NAME, MOBILE_SCAN_FORM_NAME, SELLER_REQUEST_FORM_NAME])) {
			const { fulfillDate, warningDate } = calculateDefaultDates(taskTypeId, codelist);

			sendControlNext(formName, setValue({ fieldName: FIELDS.fulfillDate, value: fulfillDate }));
			sendControlNext(formName, setValue({ fieldName: FIELDS.warningDate, value: warningDate }));
		}
	};

export const startupTaskCreatingPages =
	(formName) =>
	(dispatch, getState, { sendControlNext }) => {
		sendControlNext(formName, resetForm());
		dispatch(startupDetailInfo(formName));
	};

export const searchPartner =
	(formName) =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const formData = await getFormData(formName);

		NiceModal.show(MODALS.client_search, {
			formData,
			onSelect: (partner) => {
				const { address, ...otherPartnerValues } = partner;
				const relevantAddressValues = pickAll(['municipality', 'street', 'streetNo', 'zipCode'], address);
				const relevantPartnerValues = pickAll(
					['businessIn', 'businessName', 'email', 'firstName', 'surname'],
					otherPartnerValues,
				);

				const fieldsToUpdate = {
					...relevantAddressValues,
					...relevantPartnerValues,
					title: otherPartnerValues?.titlePrefix,
					phoneNumber: formatPhoneNumber(otherPartnerValues?.phone),
					birthDate: fromIsoDateToDate(otherPartnerValues?.birthDate),
					personIn: otherPartnerValues?.birthNumber,
				};

				const finalData = {
					...formData,
					...fieldsToUpdate,
				};

				// clean reset - also gets rid of validations and errors
				sendControlNext(formName, resetForm());
				sendControlNext(formName, replaceData({ data: finalData }));
				dispatch(setPartnerEditability(false));
			},
		});
	};

export const resetClientInfo =
	(formName) =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const originalFormData = await getFormData(formName);
		const formDataWithoutPartnerFields = omit(partnerFields, originalFormData);

		// clean reset - also gets rid of validations and errors
		sendControlNext(formName, resetForm());
		sendControlNext(formName, replaceData({ data: formDataWithoutPartnerFields }));
		dispatch(setPolicyCompanyId(null));
		dispatch(setSelectedClient(null));
		dispatch(setSelectedPolicy(null));
		dispatch(setPartnerEditability(true));
		dispatch(setPolicyEditability(true));
	};

export const searchPS =
	(formName) =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const formData = await getFormData(formName);

		if (includes(formName, [SELLER_REQUEST_FORM_NAME, MOBILE_SCAN_FORM_NAME])) {
			if (isNilOrEmptyString(formData?.policyNumber) && isNilOrEmptyString(formData?.proposalNumber)) {
				return dispatch(dialog.actions.open(dialogs.NO_PS_NUMBER, null, dialog.constants.WARNING));
			}
		} else {
			if (isNilOrEmptyString(formData?.policyNumber)) {
				return dispatch(dialog.actions.open(dialogs.NO_PS_NUMBER, null, dialog.constants.WARNING));
			}
		}

		const backOfficeSegmentCodelist = getCodelistByName(codelists.backOfficeSegment)(getState());

		dispatch(dialog.actions.close(dialogs.NO_PS_NUMBER));
		NiceModal.show(MODALS.policy_search, {
			policyNo: formData?.policyNumber,
			proposalNo: formData?.proposalNumber,
			onSelect: (policy, policyStatus, partner = {}) => {
				const { policyHolder, policyNo, proposalNo, systemId } = policy;
				const backOfficeSegmentValue = findBackOfficeSegmentValue(systemId, backOfficeSegmentCodelist);

				dispatch(setPartnerEditability(false));
				dispatch(setPolicyEditability(false));

				const fieldsToUpdatePSInfo = {
					policyHolderFirstName: policyHolder?.firstName,
					policyHolderSurname: policyHolder?.surname,
					policyHolderBusinessName: policyHolder?.businessName,
					policyNumber: String(policyNo),
					proposalNumber: proposalNo,
					policyStatus,
					backOfficeSegment: backOfficeSegmentValue,
				};

				const { address, ...otherPartnerValues } = partner;
				const relevantAddressValues = pickAll(['municipality', 'street', 'streetNo', 'zipCode'], address);
				const relevantPartnerValues = pickAll(
					['businessIn', 'businessName', 'email', 'firstName', 'surname'],
					otherPartnerValues,
				);

				const fieldsToUpdateClientInfo = {
					...relevantAddressValues,
					...relevantPartnerValues,
					title: otherPartnerValues?.titlePrefix,
					phoneNumber: formatPhoneNumber(otherPartnerValues?.phone),
					birthDate: fromIsoDateToDate(otherPartnerValues?.birthDate),
					personIn: otherPartnerValues?.birthNumber,
				};

				const finalData = {
					...formData,
					...fieldsToUpdatePSInfo,
					...fieldsToUpdateClientInfo,
				};

				// clean reset - also gets rid of validations and errors
				sendControlNext(formName, resetForm());
				sendControlNext(formName, replaceData({ data: finalData }));
			},
		});
	};

export const submitDocumentId =
	(formName) =>
	async (dispatch, getState, { getFormFieldValue, sendControlNext }) => {
		const documentId = await getFormFieldValue({
			formName,
			fieldName: FIELDS.documentId,
		});

		if (!documentId || documentId.trim() === '') return;
		// user may type whole ID (with "D" character) - BE wants clean ID, so we are getting rid of it
		const cleanDocumentId = documentId.replace(/d/gi, '');

		dispatch(dialog.actions.close(dialogs.DOCUMENT_NOT_FOUND_DIALOG));
		dispatch(
			fetch.actions.get(fetch.constants.urls.checkTaskAttachment, {
				queryParams: {
					documentId: cleanDocumentId,
				},
				onSuccess: [
					() => () => {
						dispatch(addDocumentId(documentId));
						sendControlNext(
							SELLER_REQUEST_FORM_NAME,
							setValue({ fieldName: FIELDS.documentId, value: null }),
						);
					},
				],
				onError: [
					() => () => {
						dispatch(dialog.actions.open(dialogs.DOCUMENT_NOT_FOUND_DIALOG));
					},
				],
				shouldReportError: ({ failure }) => {
					return failure.status !== 404;
				},
			}),
		);
	};

export const uploadFiles =
	({ formName, taskId }) =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(formName);

		const files = propOr(EMPTY_VALUES.ARRAY, FIELDS.fileUpload)(formData);
		const documentIds = getDocumentIds(getState());
		const userLogin = getCduUserLogon(getState());

		const body = {
			emails: [],
			taskId,
			notifyManagers: true,
			userLogin,
		};

		// check for documentIds
		// if documentIds exist > upload them as whole array
		// if no documentIds > skip and call email notif in the end
		if (!isEmpty(documentIds)) {
			const cleanedDocumentIds = documentIds.map((docId) => docId.replace(/d/gi, ''));
			await dispatch(
				fetch.actions.post(fetch.constants.urls.createTaskAttachment, {
					replaceParams: { id: taskId },
					body: {
						documentIds: cleanedDocumentIds,
					},
				}),
			);
		}

		// check for files
		// if files exist > upload them individualy > after all upload promises resolve > call email notif endpoint
		if (!isNilOrEmpty(files)) {
			const filesWithRetrievedBlobs = await getFilesFromBlobUrls(files);

			const filesWithActualFilesFromBlobs = filesWithRetrievedBlobs.map(({ file, fileName }) => {
				const newFile = new File([file], fileName, {
					type: file.type,
				});

				const formData = new FormData();
				formData.append('file', newFile);
				return formData;
			});

			const promises = filesWithActualFilesFromBlobs.map(async (file) => {
				await dispatch(
					fetch.actions.uploadData(fetch.constants.urls.createTaskAttachment, {
						replaceParams: { id: taskId },
						body: file,
					}),
				);
			});

			// after all promises of file upload are resolved, call email notif with body
			return Promise.all([...promises]).then(() => {
				dispatch(
					fetch.actions.post(fetch.constants.urls.taskEmailCopy, {
						body,
					}),
				);
			});
		}

		// call email notif everytime when task is created - even if no documentIds or file attachments
		dispatch(
			fetch.actions.post(fetch.constants.urls.taskEmailCopy, {
				body,
			}),
		);
	};

export const tryCreatingTask =
	(formName) =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const formData = await getFormData(formName);
		const isTask = formName !== MANUAL_LEAD_FORM_NAME;

		const policyCompanyId = getPolicyCompanyId(getState());
		const documentIds = getDocumentIds(getState());
		const files = propOr(EMPTY_VALUES.ARRAY, FIELDS.fileUpload)(formData);
		const haveAttachments = !isNilOrEmpty(documentIds) || !isNilOrEmpty(files);

		// close and reset all the "required" errors
		dispatch(dialog.actions.close(formName));
		dispatch(resetErrorsByPage({ page: formName }));

		// block taskCreation if user is on backOffice + has attachments + fetched policyNo is old GP type
		if (formName === BACKOFFICE_FORM_NAME && policyCompanyId === COMPANY.GLI && haveAttachments) {
			dispatch(addSingleErrorByPageAndId({ page: formName, errors: ['backOfficeWithGPPolicyNoAndAttachments'] }));
			return dispatch(dialog.actions.open(formName));
		} else {
			dispatch(removeErrorByPageAndId({ page: formName, id: 'backOfficeWithGPPolicyNoAndAttachments' }));
		}

		const userDatafromDB = getUserDataFromDBFlag(getState());

		if (formData?.radio === radioOptionsIds.users && !userDatafromDB) {
			const result = await dispatch(
				userSearch.actions.searchUser({
					type: searchUserTypes.USER_LIST_SEARCH,
					formData: {
						login: formData?.login,
						firstName: formData?.userFirstName,
						surname: formData?.userSurname,
						sellerIn: formData?.sellerIn,
					},
				}),
			);

			const data = result?.body?.data;

			if (result && !isNilOrEmpty(data) && data.length === 1) {
				const { login, sellerIn, firstName, surname } = data[0];

				const fieldsToUpdateResolverSettings = {
					login,
					sellerIn,
					userFirstName: firstName,
					userSurname: surname,
				};

				forEachObjIndexed((value, key) => {
					sendControlNext(formName, setValue({ fieldName: key, value }));
				}, fieldsToUpdateResolverSettings);
			} else {
				dispatch(
					dialog.actions.open(resolverSettingsDialogs.NON_UNIQUE_SOLVER, null, dialog.constants.WARNING),
				);
				return false;
			}
		}

		dispatch(dialog.actions.close(dialogs.DOCUMENT_NOT_FOUND_DIALOG));
		dispatch(dialog.actions.close(dialogs.NO_PS_NUMBER));
		dispatch(dialog.actions.close(resolverSettingsDialogs.NON_UNIQUE_SOLVER));

		NiceModal.show(MODALS.lightbox_modal, {
			title: isTask
				? getMessage('lightBox:taskCreatingPagesNewTask')
				: getMessage('lightBox:taskCreatingPagesNewLead'),
			onConfirm: () => dispatch(isTask ? checkPS(formName) : createTask(formName)),
		});
	};

const checkPS =
	(formName) =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(formName);
		if (formData?.policyNumber > 0) {
			dispatch(createTask(formName));
		} else {
			NiceModal.show(MODALS.lightbox_modal, {
				title: getMessage('lightBox:sellerRequestCheckPS'),
				onConfirm: () => dispatch(createTask(formName)),
			});
		}
	};

const createTask =
	(formName) =>
	async (dispatch, getState, { getFormData, sendControlNext }) => {
		const formData = await getFormData(formName);
		const selectedClientFromSearch = getSelectedClient(getState());
		const selectedPolicyFromSearch = getSelectedPolicy(getState());
		const body = bodyFactory({ formData, selectedClientFromSearch, selectedPolicyFromSearch });

		const isTask = formName !== MANUAL_LEAD_FORM_NAME;
		const url = isTask ? fetch.constants.urls.task : fetch.constants.urls.createLead;

		dispatch(setTaskIsBeingCreated(true));
		dispatch(
			fetch.actions.post(url, {
				body,
				onSuccess: [
					// eslint-disable-next-line
					(response) => async () => {
						const taskId = response.body;
						isTask && (await dispatch(uploadFiles({ formName, taskId })));
						sendControlNext(formName, resetForm());
						dispatch(reset());
						dispatch(startupDetailInfo(formName));
						const toastMessage = isTask ? 'toastr:approveCreateNewTask' : 'toastr:approveCreateNewLead';
						enqueueSnackbar(getMessage(toastMessage), { variant: 'success' });
					},
				],
				onError: [
					() => {
						dispatch(setTaskIsBeingCreated(false));
					},
				],
			}),
		);
	};
